import { ACCESS_COOKIE_NAME } from '@pequity/shared/types/constants';

const ACCESS_TOKEN_KEY = 'access_token';
const ACCESS_TOKEN_CHANGED_EVENT = 'pequity:access-token-changed';

const notifyAccessTokenChanged = (): void => {
  window.dispatchEvent(new Event(ACCESS_TOKEN_CHANGED_EVENT));
};

export const getAccessToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

export const setAccessToken = (token: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
  document.cookie = `${ACCESS_COOKIE_NAME}=Bearer ${token}; path=/;`;
  notifyAccessTokenChanged();
};

export const removeAccessToken = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  notifyAccessTokenChanged();
};

export const onAccessTokenChanged = (callback: () => void): (() => void) => {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === ACCESS_TOKEN_KEY) callback();
  };

  window.addEventListener(ACCESS_TOKEN_CHANGED_EVENT, callback);
  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener(ACCESS_TOKEN_CHANGED_EVENT, callback);
    window.removeEventListener('storage', handleStorage);
  };
};

// JWT payloads are Base64URL-encoded ('-'/'_', no padding); atob decodes standard Base64 and
// throws on the URL alphabet, so normalize before decoding.
const decodeJwtPayload = (token: string): { exp?: unknown } | null => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

export const getTokenExpMs = (token: string): number | null => {
  const payload = decodeJwtPayload(token);
  return payload && typeof payload.exp === 'number' ? payload.exp * 1000 : null;
};

export const tokenExpired = (token: string): boolean => {
  if (!token) return true;
  const expMs = getTokenExpMs(token);
  if (expMs === null) return true;
  return expMs - 15000 < Date.now();
};

export const hasValidToken = (): boolean => {
  const token = getAccessToken();
  return !!token && !tokenExpired(token);
};
